import { ValueType } from '@aloft/toolkit-shared';

import { AdditionalFeatureKey, DetailField, SalesComparisonColumns } from './types.ts';

export const salesComparisonColumns: SalesComparisonColumns = [
  { key: ['dataSource'], label: ['Data Source'] },
  { key: ['propertyType'], label: ['Property Type'] },
  { key: ['proximity'], label: ['Proximity'] },
  { key: ['yearBuilt'], label: ['Year Built'] },
  { key: ['salesPrice'], label: ['Sales Price'] },
  { key: ['listingPrice'], label: ['Listing Price'] },
  { key: ['dom'], label: ['CDOM'] },
  {
    key: ['contractDate', 'saleDate'],
    label: ['Contract Date', 'Sale Date'],
    adjustment: 'saleDateAdjustment',
  },
  {
    key: ['siteSize', 'siteInfluence', 'view'],
    label: ['Site Size', 'Site Influence', 'View'],
    adjustment: 'siteSizeAdjustment',
  },
  { key: ['quality'], label: ['Quality'], adjustment: 'qualityAdjustment' },
  { key: ['condition'], label: ['Condition'], adjustment: 'conditionAdjustment' },
  { key: ['gla'], label: ['GLA'], adjustment: 'glaAdjustment' },
  { key: ['bedCount'], label: ['Bed Count'], adjustment: 'bedCountAdjustment' },
  { key: ['bathCount'], label: ['Bath Count'], adjustment: 'bathCountAdjustment' },
  { key: ['additionalFeatures.basement'], label: ['Basement'], adjustment: 'basementAdjustment' },
  {
    key: ['additionalFeatures.basementBathCount'],
    label: ['Bath Count (Basement)'],
    adjustment: 'basementBathCountAdjustment',
  },
  {
    key: ['additionalFeatures.garage'],
    label: ['Garage / Carport'],
    adjustment: 'garageAdjustment',
  },
  {
    key: ['additionalFeatures.porch'],
    label: ['Porch / Patio / Deck'],
    adjustment: 'porchAdjustment',
  },
  { key: ['additionalFeatures.attic'], label: ['Attic'], adjustment: 'atticAdjustment' },
  {
    key: ['additionalFeatures.fireplace'],
    label: ['Fireplace'],
    adjustment: 'fireplaceAdjustment',
  },
  { key: ['additionalFeatures.shed'], label: ['Shed'], adjustment: 'shedAdjustment' },
  { key: ['additionalFeatures.barn'], label: ['Barn'], adjustment: 'barnAdjustment' },
  { key: ['additionalFeatures.adu'], label: ['ADU'], adjustment: 'aduAdjustment' },
  { key: ['additionalFeatures.pool'], label: ['Pool / Spa'], adjustment: 'poolAdjustment' },
  { key: ['additionalFeatures.workshop'], label: ['Workshop'], adjustment: 'workshopAdjustment' },
  {
    key: ['totalCostToCureDisplayName'],
    label: ['Total Cost to Cure'],
    adjustment: 'totalCostToCureAdjustment',
  },
  { key: ['otherAdjustmentsDisplayName'], label: ['Other'], adjustment: 'otherAdjustments' },
  {
    key: ['netAdj', 'grossAdj'],
    label: ['Net Adj.', 'Gross Adj.'],
    adjustment: 'netAdjPrice',
    borderTop: true,
  },
  {
    key: ['adjSalesPrice'],
    label: ['Adj. Sales Price'],
    bold: true,
    borderTop: true,
    align: 'right',
  },
];

export const ADDITIONAL_FEATURE_KEYS: AdditionalFeatureKey[] = [
  'additionalFeatures.basement',
  'additionalFeatures.basementBathCount',
  'additionalFeatures.garage',
  'additionalFeatures.porch',
  'additionalFeatures.attic',
  'additionalFeatures.fireplace',
  'additionalFeatures.pool',
  'additionalFeatures.shed',
  'additionalFeatures.barn',
  'additionalFeatures.adu',
  'additionalFeatures.workshop',
];

export const ADDITIONAL_FEATURE_LABELS: Record<AdditionalFeatureKey, string> = {
  'additionalFeatures.basement': 'Basement',
  'additionalFeatures.basementBathCount': 'Bath Count (Basement)',
  'additionalFeatures.garage': 'Garage / Carport',
  'additionalFeatures.porch': 'Porch / Patio / Deck',
  'additionalFeatures.attic': 'Attic',
  'additionalFeatures.fireplace': 'Fireplace',
  'additionalFeatures.pool': 'Pool / Spa',
  'additionalFeatures.shed': 'Shed',
  'additionalFeatures.barn': 'Barn',
  'additionalFeatures.adu': 'ADU',
  'additionalFeatures.workshop': 'Workshop',
};

export const propertyDetailFields = (
  address?: string,
  propertyType?: string,
  locationType?: string,
  zoning?: string,
  zoningCompliance?: string,
  parcelNumber?: string,
  newConstruction?: boolean,
  propertyInterest?: string,
  hoa?: string,
  hoaFees?: string,
  pud?: string,
  legalDescription?: string,
): DetailField[] => [
  { key: 'address', label: 'Subject Property Address', getValue: () => address },
  { key: 'propertyType', label: 'Property Type', getValue: () => propertyType },
  { key: 'locationType', label: 'Location Type', getValue: () => locationType },
  { key: 'zoning', label: 'Zoning', getValue: () => zoning },
  { key: 'zoningCompliance', label: 'Zoning Compliance', getValue: () => zoningCompliance },
  {
    key: 'parcelNumber',
    label: 'Parcel Number',
    getValue: () => (newConstruction ? 'New Construction' : parcelNumber),
  },
  {
    key: 'propertyInterest',
    label: 'Property Interest',
    getValue: () => propertyInterest,
  },
  { key: 'hoa', label: 'Neighborhood Name', getValue: () => hoa },
  { key: 'hoaFees', label: 'Mandatory Fees', getValue: () => hoaFees },
  { key: 'pud', label: 'PUD', getValue: () => pud },
  { key: 'legalDescription', label: 'Legal Description', getValue: () => legalDescription },
];

export const orderDetailFields = (
  clientName?: string,
  clientAddress?: string,
  borrowerName?: string,
  appraisalType?: string,
  orderNumber?: string,
  inspectionDate?: string,
  effectiveDate?: string,
  intendedUsers?: string,
  intendedUse?: string,
): DetailField[] => [
  { key: 'clientName', label: 'Client Name', getValue: () => clientName },
  { key: 'clientAddress', label: 'Client Address', getValue: () => clientAddress },
  { key: 'borrowerName', label: 'Borrower Name', getValue: () => borrowerName },
  { key: 'appraisalType', label: 'Product Type', getValue: () => appraisalType },
  { key: 'orderNumber', label: 'Order Number', getValue: () => orderNumber },
  { key: 'inspectionDate', label: 'Inspection Date', getValue: () => inspectionDate },
  { key: 'effectiveDate', label: 'Effective Date', getValue: () => effectiveDate },
  { key: 'intendedUsers', label: 'Intended User(s)', getValue: () => intendedUsers },
  { key: 'intendedUse', label: 'Intended Use', getValue: () => intendedUse },
];

export const valueTypeLabels: Record<ValueType, string> = {
  'as-is': 'As-Is',
  'subject-to': 'Subject-To',
};
